// Profile Confidence Level Utilities
// Standardized colors and ordering for consistent use across the application

// Type definitions
export interface ProfileConfidenceConfig {
  level: string;
  label: string;
  description: string;
  color: string;
  bgColor: string;
  borderColor: string;
  textColor: string;
  order: number;
}

// Standardized Profile Confidence Configuration
export const PROFILE_CONFIDENCE_CONFIG: Record<string, ProfileConfidenceConfig> = {
  HIGH: {
    level: 'HIGH',
    label: 'High',
    description: 'ProfileConfidenceScore is 100%',
    color: 'bg-green-500',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    textColor: 'text-green-800',
    order: 1
  },
  MEDIUM: {
    level: 'MEDIUM',
    label: 'Medium',
    description: 'ProfileConfidenceScore is greater than or equal to 75% and less than 100%',
    color: 'bg-blue-500',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-800',
    order: 2
  },
  LOW: {
    level: 'LOW',
    label: 'Low',
    description: 'ProfileConfidenceScore is greater than or equal to 50% and less than 75%',
    color: 'bg-orange-500',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
    textColor: 'text-orange-800',
    order: 3
  },
  NO: {
    level: 'NO',
    label: 'No Match',
    description: 'ProfileConfidenceScore is less than 50%',
    color: 'bg-red-500',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
    textColor: 'text-red-800',
    order: 4
  }
};

// Get ordered profile confidence levels
export const getOrderedProfileConfidenceLevels = (): ProfileConfidenceConfig[] => {
  return Object.values(PROFILE_CONFIDENCE_CONFIG).sort((a, b) => a.order - b.order);
};

// Get profile confidence config by level
export const getProfileConfidenceConfig = (level?: string | null): ProfileConfidenceConfig => {
  const normalizedLevel = level?.toUpperCase();
  return PROFILE_CONFIDENCE_CONFIG[normalizedLevel || ''] || PROFILE_CONFIDENCE_CONFIG.NO;
};

// Get badge classes for profile confidence
export const getProfileConfidenceBadgeClasses = (level?: string | null): string => {
  const config = getProfileConfidenceConfig(level);
  return `${config.bgColor} ${config.textColor} ${config.borderColor}`;
};

// Get profile confidence label
export const getProfileConfidenceLabel = (level?: string | null): string => {
  const config = getProfileConfidenceConfig(level);
  return config.label;
};

// Check if a configuration item is a profile confidence level
export const isProfileConfidenceLevel = (configName: string): boolean => {
  return configName.startsWith('PROFILECONFIDENCELEVEL_');
};

// Extract profile confidence level from config name
export const extractProfileConfidenceLevel = (configName: string): string | null => {
  if (!isProfileConfidenceLevel(configName)) return null;
  
  const level = configName.replace('PROFILECONFIDENCELEVEL_', '');
  return Object.keys(PROFILE_CONFIDENCE_CONFIG).includes(level) ? level : null;
};

// Convert numerical confidence score to profile confidence level
export const getConfidenceLevelFromScore = (score: number | string): string => {
  // Convert to number if it's a string
  const numScore = typeof score === 'string' ? parseFloat(score) : score;
  
  // Convert to percentage if it's a decimal (0-1 range)
  const percentage = numScore <= 1 ? numScore * 100 : numScore;
  
  if (percentage === 100) return 'HIGH';
  if (percentage >= 75) return 'MEDIUM';
  if (percentage >= 50) return 'LOW';
  return 'NO';
};